import {createContext, useCallback, useContext, useMemo, useState} from 'react'

import {type AgeAssuranceRedirectDialogState} from '#/components/ageAssurance/AgeAssuranceRedirectDialog'
import * as Dialog from '#/components/Dialog'
import {type Screen} from '#/components/dialogs/EmailDialog/types'
import {type ReportSubject} from '#/components/moderation/ReportDialog'

type Control = Dialog.DialogControlProps

export type StatefulControl<T> = {
  control: Control
  open: (value: T) => void
  clear: () => void
  value: T | undefined
}

type WIDUpgradeControl = {
  isOpen: boolean
  open: () => void
  close: () => void
}

type ControlsContext = {
  mutedWordsDialogControl: Control
  signinDialogControl: Control
  inAppBrowserConsentControl: StatefulControl<string>
  emailDialogControl: StatefulControl<Screen>
  linkWarningDialogControl: StatefulControl<{
    href: string
    displayText: string
    share?: boolean
  }>
  ageAssuranceRedirectDialogControl: StatefulControl<AgeAssuranceRedirectDialogState>
  reportDialogControl: StatefulControl<{subject: ReportSubject}>
  widUpgradeControl: WIDUpgradeControl
  widLockByAgeControl: WIDUpgradeControl
}

const ControlsContext = createContext<ControlsContext | null>(null)
ControlsContext.displayName = 'GlobalDialogControlsContext'

export function useGlobalDialogsControlContext() {
  const ctx = useContext(ControlsContext)
  if (!ctx) {
    throw new Error(
      'useGlobalDialogsControlContext must be used within a Provider',
    )
  }
  return ctx
}

export function useWIDUpgradeControl(): WIDUpgradeControl {
  const {widUpgradeControl} = useGlobalDialogsControlContext()
  return widUpgradeControl
}
export function useWIDLockByAgeUpgradeControl(): WIDUpgradeControl {
  const {widLockByAgeControl} = useGlobalDialogsControlContext()
  return widLockByAgeControl
}

export function Provider({children}: React.PropsWithChildren<{}>) {
  const mutedWordsDialogControl = Dialog.useDialogControl()
  const signinDialogControl = Dialog.useDialogControl()
  const inAppBrowserConsentControl = useStatefulDialogControl<string>()
  const emailDialogControl = useStatefulDialogControl<Screen>()
  const linkWarningDialogControl = useStatefulDialogControl<{
    href: string
    displayText: string
    share?: boolean
  }>()
  const ageAssuranceRedirectDialogControl =
    useStatefulDialogControl<AgeAssuranceRedirectDialogState>()
  const reportDialogControl = useStatefulDialogControl<{
    subject: ReportSubject
  }>()
  const [widUpgradeOpen, setWidUpgradeOpen] = useState(false)
  const [widLockByAgeOpen, setWidLockByAgeOpenOpen] = useState(false)
  const openWIDUpgrade = useCallback(() => setWidUpgradeOpen(true), [])
  const closeWIDUpgrade = useCallback(() => setWidUpgradeOpen(false), [])
  const openWidLockByAgeUpgrade = useCallback(
    () => setWidLockByAgeOpenOpen(true),
    [],
  )
  const closeWidLockByAgeUpgrade = useCallback(
    () => setWidLockByAgeOpenOpen(false),
    [],
  )

  const widUpgradeControl = useMemo<WIDUpgradeControl>(
    () => ({
      isOpen: widUpgradeOpen,
      open: openWIDUpgrade,
      close: closeWIDUpgrade,
    }),
    [widUpgradeOpen, openWIDUpgrade, closeWIDUpgrade],
  )
  const widLockByAgeControl = useMemo<WIDUpgradeControl>(
    () => ({
      isOpen: widLockByAgeOpen,
      open: openWidLockByAgeUpgrade,
      close: closeWidLockByAgeUpgrade,
    }),
    [widLockByAgeOpen, openWidLockByAgeUpgrade, closeWidLockByAgeUpgrade],
  )

  const ctx = useMemo<ControlsContext>(
    () => ({
      mutedWordsDialogControl,
      signinDialogControl,
      inAppBrowserConsentControl,
      emailDialogControl,
      linkWarningDialogControl,
      ageAssuranceRedirectDialogControl,
      reportDialogControl,
      widUpgradeControl,
      widLockByAgeControl,
    }),
    [
      mutedWordsDialogControl,
      signinDialogControl,
      inAppBrowserConsentControl,
      emailDialogControl,
      linkWarningDialogControl,
      ageAssuranceRedirectDialogControl,
      reportDialogControl,
      widUpgradeControl,
      widLockByAgeControl,
    ],
  )

  return (
    <ControlsContext.Provider value={ctx}>{children}</ControlsContext.Provider>
  )
}

export function useStatefulDialogControl<T>(
  initialValue?: T,
): StatefulControl<T> {
  const [value, setValue] = useState(initialValue)
  const control = Dialog.useDialogControl()
  return useMemo(
    () => ({
      control,
      open: (v: T) => {
        setValue(v)
        control.open()
      },
      clear: () => setValue(initialValue),
      value,
    }),
    [control, value, initialValue],
  )
}
